/**
 * Every client-facing route on the backend, once each.
 *
 * The types are transcribed from the handlers and the projections they select —
 * src/auth/service.ts, src/me/routes.ts, src/quiz/service.ts,
 * src/billing/service.ts, src/consent/service.ts, src/enrolment/service.ts,
 * src/household/service.ts, src/media/photos.ts, src/media/clusters.ts,
 * src/reports/service.ts. Where a handler itself returns `unknown` (photos,
 * hits) the interface here is read off the SQL projection; every screen also
 * shows the raw body, so a drift shows up rather than being papered over.
 *
 * DELIBERATELY ABSENT: /v1/attribute, /v1/search/*, /v1/seeds,
 * /v1/subjects/{user_ref} are the services proxy surface, not client-facing;
 * /public/email/verify and /public/consent/{token}/confirm are opened from a
 * mail client, not from here; webhooks belong to providers.
 */
import { apiBlob, apiRequest, newIdempotencyKey, putToPresignedUrl, setTokens, type TokenPair } from './client.js';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface OtpChallenge {
  challenge_id: string;
  expires_at: string;
  resend_after: string;
}

export interface OnboardingState {
  next_step: string | null;
  blocking_coverage: string[] | null;
  /**
   * Steps this person explicitly skipped and has not since completed. Server
   * state, so it survives a sign-out and is the same on every device — which is
   * the whole reason skipOnboardingStep exists rather than a localStorage flag.
   * Never null: the view coalesces the empty case to an empty array.
   */
  skipped_steps: string[];
  quiz_completed: boolean;
  seat_claimed: boolean;
  profile_complete: boolean;
  consent_signed: boolean;
  enrolled: boolean;
  email_verified: boolean;
  photos_uploaded: boolean;
  notifications_enabled: boolean;
}

export interface SessionSummary {
  session_id: string;
  device_platform: string | null;
  app_version: string | null;
  created_at: string;
  last_seen_at: string;
  is_current: boolean;
}

/** 202. `phone_e164` is the ONLY route on the whole surface that takes a phone. */
export function requestOtp(phoneE164: string): Promise<OtpChallenge> {
  return apiRequest<OtpChallenge>('POST', '/v1/auth/otp', {
    body: { phone_e164: phoneE164 },
    anonymous: true,
  });
}

/** Stores the pair as a side effect: nothing else in the app may hold tokens. */
export async function verifyOtp(
  challengeId: string,
  code: string,
): Promise<TokenPair & { onboarding: OnboardingState }> {
  const result = await apiRequest<TokenPair & { onboarding: OnboardingState }>(
    'POST',
    '/v1/auth/otp/verify',
    { body: { challenge_id: challengeId, code }, anonymous: true },
  );
  setTokens(result);
  return result;
}

export function logout(): Promise<void> {
  return apiRequest<void>('POST', '/v1/auth/logout');
}

/**
 * Records that this person walked past a step, and returns the state that
 * results — so the caller routes off this response instead of refetching
 * /v1/me for the value it just changed.
 *
 * Only the five `skippable` steps are accepted: 'quiz', 'plan', 'profile_extra',
 * 'photos', 'notifications'. The others (profile, consent, enrolment, email)
 * answer 403 STEP_NOT_SKIPPABLE — they are what coverage depends on.
 */
export function skipOnboardingStep(
  stepKey: 'quiz' | 'plan' | 'profile_extra' | 'photos' | 'notifications',
): Promise<{ onboarding: OnboardingState }> {
  return apiRequest<{ onboarding: OnboardingState }>('POST', '/v1/onboarding/skips', {
    body: { step_key: stepKey },
  });
}

export function listSessions(): Promise<{ sessions: SessionSummary[] }> {
  return apiRequest<{ sessions: SessionSummary[] }>('GET', '/v1/auth/sessions');
}

export function revokeSession(sessionId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/v1/auth/sessions/${encodeURIComponent(sessionId)}`);
}

/** A registered push device (the phone app's FCM token). This browser never
 *  registers one — there is no web push route — so this list is read-only here. */
export interface DeviceView {
  token_id: string;
  platform: string;
  timezone: string | null;
  created_at: string;
  last_seen_at: string;
}

export function listDevices(): Promise<{ devices: DeviceView[] }> {
  return apiRequest<{ devices: DeviceView[] }>('GET', '/v1/devices');
}

// ---------------------------------------------------------------------------
// Me, profile, email
// ---------------------------------------------------------------------------

export interface PersonBlock {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  /** The VERIFIED address. Null until the link is clicked. */
  email: string | null;
  email_verified: boolean;
  /** The address awaiting its link, or null. Resend and prefill from this
   *  first: for a first-time user `email` is null until they verify. */
  pending_email: string | null;
  date_of_birth: string | null;
  employer_name: string | null;
  occupation: string | null;
  school_name: string | null;
}

export interface Me {
  account: { phone_e164: string | null; status: string };
  person: PersonBlock;
  consent_eligibility: { required_signer_role: string | null; blocked_reason: string | null };
  onboarding: OnboardingState;
  /** The paywall's predicate (0023) — the reports surface, and nothing else. */
  entitlement: Entitlement;
  household: {
    household_id: string;
    role: string;
    plan_code: string;
    seat_limit: number;
    seats_used: number;
  } | null;
  score: ScoreSummary | null;
}

export function getMe(): Promise<Me> {
  return apiRequest<Me>('GET', '/v1/me');
}

/**
 * Three states per field and the client must be able to express all three:
 * key absent = unchanged, null = clear, value = set (src/me/profile.ts). So the
 * patch is passed through as given — never widened to "every field every time",
 * which is the legacy bug that endpoint exists to kill.
 */
export type ProfilePatch = Partial<
  Record<
    'first_name' | 'last_name' | 'date_of_birth' | 'employer_name' | 'occupation' | 'school_name',
    string | null
  >
>;

export function patchProfile(
  patch: ProfilePatch,
): Promise<{ person: PersonBlock; onboarding: OnboardingState }> {
  return apiRequest<{ person: PersonBlock; onboarding: OnboardingState }>(
    'PATCH',
    '/v1/me/profile',
    { body: patch },
  );
}

/** 202. Delivery needs EMAIL_PROVIDER — `fake` on dev, so nothing arrives. */
export function requestEmailChange(email: string): Promise<unknown> {
  return apiRequest<unknown>('POST', '/v1/me/email', { body: { email } });
}

/** DELETE /v1/me — account deletion. 204 even when the protocol did not finish. */
export function deleteAccount(): Promise<void> {
  return apiRequest<void>('DELETE', '/v1/me');
}

// ---------------------------------------------------------------------------
// Quiz and score
//
// ONE SCORE. The Likeness Health Score v0.3 (src/score/) is computed
// server-side by ScoreService — the only writer — and served verbatim;
// `band` arrives labelled with its noun ("low risk") and is rendered
// VERBATIM everywhere in this client, never re-derived from `live`. The
// retired quiz-only score (the old `ScorePayload`, `/v1/protection/score`
// and friends) is gone along with the routes that served it.
// ---------------------------------------------------------------------------

export interface QuizQuestion {
  key: string;
  prompt: string;
  options: string[];
  scored: boolean;
  multi?: boolean;
  required?: boolean;
  /** A conditional question's gate. `values` (plural, v3+) lists every
   *  trigger answer; `value` is v1's single-answer form. Evaluate it with
   *  flow/quiz-gate.ts — never inline, and never on one field only. */
  requires?: { key: string; value?: string; values?: string[] };
}

export function getQuiz(): Promise<{ quiz_version: string; questions: QuizQuestion[] }> {
  return apiRequest<{ quiz_version: string; questions: QuizQuestion[] }>('GET', '/v1/quiz');
}

/** src/score/service.ts#ScoreBreakdown, the wire shape toScorePayload emits. */
export interface ScoreBreakdownRead {
  quiz: { key: string; value: string; deduction: number; type: string }[];
  recovered: { key: string; points: number }[];
  escrow: {
    total: number;
    released: number;
    pending: number;
    elapsed_days: number;
    next_milestone_days: number | null;
    next_milestone_at: string | null;
    paused_reason: string | null;
  };
  misuse_decay: {
    applies: boolean;
    tier: number;
    elapsed_days: number;
    next_tier_days: number | null;
    next_tier_at: string | null;
  };
  dynamic: { id: string; active: boolean; points: number }[];
  /**
   * Points gainable by REVIEWING AND RESOLVING what was found — never phrase
   * this as points withheld or deducted (business sheet §2's own copy: "worth
   * up to N points").
   */
  locked_potential_points: number | null;
}

/**
 * `GET /v1/me/score`'s shape (src/score/service.ts#toScorePayload). The one
 * read allowed to 404 — NO_QUIZ_RESPONSE or QUIZ_OUTDATED — rather than ever
 * answering 200 with no score.
 */
export interface ScoreRead {
  score: {
    live: number;
    band: string;
    baseline: number;
    recovered: number;
    escrow_released: number;
    dynamic_deduction: number;
    current_ceiling: number;
    maximum_ceiling: number;
    scoring_version: string;
    quiz_version: string;
    computed_at: string;
    breakdown: ScoreBreakdownRead;
  };
  scope_note: string;
}

/** The compact projection carried on `GET /v1/me` and `GET /v1/home`. */
export interface ScoreSummary {
  live: number;
  band: string;
  current_ceiling: number;
  computed_at: string;
}

export function getScore(): Promise<ScoreRead> {
  return apiRequest<ScoreRead>('GET', '/v1/me/score');
}

export interface ScoreEventRead {
  event_id: string;
  trigger: string;
  component: string;
  /** Signed and untouched — the sign IS the information. */
  delta: number;
  before: number | null;
  after: number;
  created_at: string;
}

export function getScoreEvents(
  cursor?: string,
): Promise<{ events: ScoreEventRead[]; next_cursor: string | null }> {
  const query = cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`;
  return apiRequest('GET', `/v1/me/score/events${query}`);
}

export type ScoreHistoryRange = '1w' | '1m' | '3m' | '6m' | '1y' | '2y';

export const SCORE_HISTORY_RANGES: ScoreHistoryRange[] = ['1w', '1m', '3m', '6m', '1y', '2y'];

/**
 * `GET /v1/me/score/history` (src/score/service.ts#HistoryPayload).
 *
 * EVERY STRING ON IT IS SERVER-AUTHORED AND RENDERED VERBATIM: `band` carries
 * its noun, and each cause carries the sentence the api wrote. This client
 * composes none of them and paraphrases none of them — the two copy rules in
 * src/score/reasons.ts (never assert a result IS a match; never put a minus
 * sign on a subject's answer) only hold if the screen does not rewrite them.
 *
 * The client also does NO ARITHMETIC on the score: `change` arrives computed.
 */
export interface ScoreHistoryCause {
  /** A cause code, or 'unattributed' for a movement journalled before 0033. */
  code: string;
  /** Signed as the SCORE moved. The sign is the information. */
  delta: number;
  title: string;
  body: string | null;
}

export interface ScoreHistoryMovement {
  movement_id: string;
  at: string;
  delta: number;
  before: number | null;
  after: number;
  trigger: string;
  causes: ScoreHistoryCause[];
}

export interface ScoreHistoryRead {
  range: ScoreHistoryRange;
  from: string;
  to: string;
  /** Later than `from` when we hold less history than was asked for. An
   *  earlier period is UNKNOWN, not flat — say so rather than drawing it. */
  covers_from: string;
  /** Step series, oldest first. points[0] is carried in from before `from`. */
  points: { at: string; live: number }[];
  change: number;
  current: { live: number; band: string };
  movements: ScoreHistoryMovement[];
  movements_truncated: boolean;
  scope_note: string;
}

export function getScoreHistory(range: ScoreHistoryRange = '1m'): Promise<ScoreHistoryRead> {
  return apiRequest<ScoreHistoryRead>('GET', `/v1/me/score/history?range=${range}`);
}

/**
 * `POST /v1/quiz/responses` (src/quiz/service.ts#submit). Deliberately NOT
 * `ScoreRead & {response_id}`: a recompute can fail after the answers are
 * durably stored, and the api still answers 201 with `score: null` rather
 * than lose the response or 500 for a write that already succeeded — the
 * client renders "your score is being worked out" and the next `getScore()`
 * read repairs it (read-repair is `ScoreService.recompute`'s own job).
 */
export interface QuizSubmitResult {
  response_id: string;
  score: ScoreRead['score'] | null;
  scope_note: string;
}

/**
 * `subjectPersonId` names the person the answers are ABOUT, and is absent for
 * the caller's own quiz. On-device the MEMBER answers on the manager's phone,
 * so the response and the score are theirs while the manager's session is only
 * the capture context. It is a selector, never a credential — the server
 * authorises on the session and 403s a subject the caller has no authority
 * over.
 */
export function submitQuiz(
  quizVersion: string,
  answers: Record<string, string | string[]>,
  subjectPersonId?: string,
): Promise<QuizSubmitResult> {
  return apiRequest<QuizSubmitResult>('POST', '/v1/quiz/responses', {
    body: {
      quiz_version: quizVersion,
      answers,
      ...(subjectPersonId === undefined ? {} : { subject_person_id: subjectPersonId }),
    },
  });
}

// ---------------------------------------------------------------------------
// Plans and billing
// ---------------------------------------------------------------------------

export interface Plan {
  plan_code: string;
  display_name: string;
  seat_limit: number;
  tier: string;
  billing_interval: string;
  price_usd: number | null;
  apple_product_id: string | null;
  google_product_id: string | null;
  google_base_plan_id: string | null;
}

/**
 * How a plan change behaves at the store, published so the Android client asks
 * rather than hardcodes. There is no Apple block: StoreKit takes no
 * replacement mode — Apple derives the behaviour from the products' ranking in
 * the App Store Connect subscription group.
 */
export interface StorePolicy {
  google: { upgrade_replacement_mode: string; downgrade_replacement_mode: string };
}

/**
 * One offered move. The DIRECTION is the server's, not the client's: the Play
 * replacement mode depends on it, and a client deriving it from seat_limit and
 * billing_interval gets `household_monthly -> single_yearly` wrong — the seats
 * fall while the interval lengthens, so it reads as an upgrade and
 * CHARGE_FULL_PRICE then shrinks the plan immediately.
 */
export interface PlanMove {
  plan_code: string;
  direction: 'upgrade' | 'downgrade';
  /** Pass verbatim to Play's SubscriptionUpdateParams; meaningless on Apple. */
  google_replacement_mode: string;
}

export interface SubscriptionRead {
  /** Never null since 0023: every account holds at least the free plan. */
  subscription: {
    plan_code: string;
    /** false on the free plan — the only fact payment changes. */
    paid: boolean;
    provider: string;
    status: string;
    trial_ends_at: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at: string | null;
  } | null;
  seats_used: number;
  seat_limit: number | null;
  /**
   * The store's manage-subscriptions page, or null on the free plan. This is
   * where a cancellation happens: neither store lets a server do it, so the
   * app links here rather than calling an endpoint that cannot exist.
   */
  manage_url: string | null;
  /** Always null since 0023 — there is no trial. Deprecated. */
  trial: { active: boolean; ends_at: string } | null;
  /**
   * The moves from here that make sense, sorted by plan code — the server's
   * transition rule, published rather than duplicated
   * (src/billing/service.ts#planChangeRefusal). Offer these and nothing else,
   * and pass each move's own `google_replacement_mode` on Play.
   *
   * ADVISORY since 2026-09-07: the purchase happens at the store, so nothing
   * here refuses one. Empty is normal — the free plan, an annual household
   * plan (terminal), or a subscription not in good standing.
   */
  changeable_to: PlanMove[];
  /**
   * ONE server-authored sentence about the plan (spec 2026-09-10 §2). Render
   * `message` verbatim; never assemble one from the raw fields above — that is
   * how two clients end up telling two stories. Also served alone at
   * GET /v1/billing/notice for a screen that makes no other billing call.
   */
  notice: BillingNotice;
}

export interface BillingNotice {
  state: 'active' | 'ending' | 'downgrading' | 'payment_failed' | 'paused' | 'free';
  effective_at: string | null;
  headline: string;
  message: string;
  coverage: 'continues' | 'continues_reports_lock' | 'members_lose_seats';
  /** Owner only; null for a member (P20). */
  affected_members: { first_name: string | null }[] | null;
  action: {
    kind: 'manage_in_store' | 'update_payment' | 'subscribe' | 'none';
    url: string | null;
  };
}

/**
 * The api WRAPS the list: `{ plans: [...] }`, not a bare array
 * (src/billing/service.ts#readPlans). Declaring the bare array typechecked
 * against nothing — the payload crosses HTTP — and `data.map` then threw
 * `U.map is not a function`, taking the Plans screen down.
 */
export function getPlans(): Promise<{ plans: Plan[]; store_policy: StorePolicy }> {
  return apiRequest<{ plans: Plan[]; store_policy: StorePolicy }>('GET', '/v1/plans');
}

export function getSubscription(): Promise<SubscriptionRead> {
  return apiRequest<SubscriptionRead>('GET', '/v1/billing/subscription');
}

/** GET /v1/billing/notice — the notice alone, a projection of the same function. */
export function getBillingNotice() {
  return apiRequest<{ notice: BillingNotice }>('GET', '/v1/billing/notice');
}

/**
 * POST /v1/billing/purchase — the ONE billing write (spec 2026-09-07 §6).
 *
 * A browser cannot make a store purchase, so nothing in this web app calls it;
 * it exists for the type surface and for a native shell wrapping this bundle.
 * The six wrappers that used to live here — subscribe, previewPlanChange,
 * changePlan, cancelSubscription, resumeSubscription, submitAppleReceipt —
 * are gone with their routes: neither store lets a server change a plan,
 * prorate or cancel one. `manage_url` on the subscription read is where a
 * cancellation happens.
 */
export function submitPurchase(
  provider: 'apple' | 'google',
  purchaseId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return apiRequest<unknown>('POST', '/v1/billing/purchase', {
    body: { provider, purchase_id: purchaseId },
    idempotencyKey,
    retryConflict: true,
  });
}

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

export interface ConsentSubmission {
  consent_id: string;
  /** A BEARER CAPABILITY: whoever holds it can sign in the subject's name. */
  signing_url: string;
  document_version: string;
  expires_at: string | null;
}

export interface ConsentStatus {
  consent_id: string;
  status: string;
  document_version: string;
  signer_role: string;
  signed_at: string | null;
  expires_at: string | null;
}

/**
 * The body is `{ subject_person_id }` and nothing else — no signer name, because
 * a typed-in name on a consent form is not a signature. Idempotency-Key required.
 */
export function createConsentSubmission(
  subjectPersonId: string,
  idempotencyKey: string,
): Promise<ConsentSubmission> {
  return apiRequest<ConsentSubmission>('POST', '/v1/consent/submissions', {
    body: { subject_person_id: subjectPersonId },
    idempotencyKey,
    retryConflict: true,
  });
}

export function getConsent(consentId: string): Promise<ConsentStatus> {
  return apiRequest<ConsentStatus>('GET', `/v1/consent/${encodeURIComponent(consentId)}`);
}

// ---------------------------------------------------------------------------
// Enrolment
// ---------------------------------------------------------------------------

export interface CreatedSession {
  session_id: string;
  provider_session_id: string;
  region: string;
  expires_at: string;
}

export interface EnrolmentState {
  status: string;
  enrolled_at: string | null;
}

/**
 * NO Idempotency-Key — the route 400s if the header is present at all, because
 * creating a session burns a liveness attempt and a retry must create a new one
 * (src/enrolment/routes.ts:50).
 */
export function createEnrolmentSession(subjectPersonId: string): Promise<CreatedSession> {
  return apiRequest<CreatedSession>('POST', '/v1/enrolment/sessions', {
    body: { subject_person_id: subjectPersonId },
  });
}

/** Empty body by design: subject_is_adult is computed server-side, never chosen. */
export function submitEnrolmentResult(
  sessionId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return apiRequest<unknown>(
    'POST',
    `/v1/enrolment/sessions/${encodeURIComponent(sessionId)}/result`,
    { body: {}, idempotencyKey, retryConflict: true },
  );
}

export function getEnrolmentState(personId: string): Promise<EnrolmentState> {
  return apiRequest<EnrolmentState>('GET', '/v1/enrolment/state', { query: { person_id: personId } });
}

/**
 * The browser's own credential for Face Liveness — an identity token, NOT an
 * AWS credential. See src/enrolment/liveness-credentials.ts: the backend holds
 * no Rekognition credential and must never gain one (D8, D15), so the video is
 * streamed by this browser straight to Rekognition and never passes through the
 * api (P17, the same rule as photo bytes).
 */
export interface LivenessIdentity {
  identity_id: string;
  token: string;
  identity_pool_id: string;
  region: string;
}

/**
 * NO subject argument and NO Idempotency-Key. The person comes from the session
 * and nothing else (P1) — accepting a subject would let a caller mint a capture
 * credential attributed to someone else, and attribution is the point: the
 * per-person attempt ceiling cannot count an anonymous attempt. Each call mints
 * a fresh 15-minute token, so there is nothing a replay would buy
 * (src/enrolment/routes.ts:152).
 */
export function mintLivenessCredentials(): Promise<LivenessIdentity> {
  return apiRequest<LivenessIdentity>('POST', '/v1/enrolment/liveness-credentials');
}

// ---------------------------------------------------------------------------
// Household
// ---------------------------------------------------------------------------

/**
 * A face box normalised 0–1 from the image's top-left, as Rekognition returns
 * it. Carried by `pfp_focus` so a profile picture copied out of a family shot
 * can be cropped to the RIGHT face — centre-cropping a three-person photo picks
 * whoever stands in the middle.
 */
export interface FaceBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface HouseholdMember {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  has_own_device: boolean;
  email: string | null;
  email_verified: boolean;
  /** Where this member's verification (and so consent) link went, until
   *  they click it. The manager's only view of it for an on-device member. */
  pending_email: string | null;
  consent_status: string;
  enrolment_status: string;
  monitoring_enabled: boolean;
  covered: boolean;
  active_hits: number;
  /** A presigned GET signed AT READ TIME, never stored pre-signed. `null` is
   *  the ordinary state (P24) — render initials, not an error. */
  pfp_url: string | null;
  /** When `pfp_url` stops working. The field is the authority, not the TTL
   *  behind it: do not cache or persist the URL past this. */
  pfp_expires_at: string | null;
  /** `null` for a directly-uploaded picture, or before attribution has run —
   *  fall back to a centre crop. */
  pfp_focus: FaceBox | null;
  /** MAY THIS CALLER OPEN THAT MEMBER'S PHOTOS. Server-computed from
   *  `has_own_device` AND whether the caller owns the household, so it is not
   *  re-derivable here: a household MEMBER reading the roster sees the other
   *  members and must not be offered their photos. Read it; never compute it.
   *  It is also what gates the profile-picture WRITE — the api authorises both
   *  through the same predicate (PhotoService.resolveSubject). */
  photos_visible: boolean;
}

export interface PendingInvite {
  invite_id: string;
  invitee_email: string | null;
  created_at: string;
  expires_at: string;
  send_count: number;
  last_sent_at: string | null;
}

export interface HouseholdRead {
  household_id: string;
  plan_code: string | null;
  seat_limit: number | null;
  seats_used: number;
  members: HouseholdMember[];
  pending_invites: PendingInvite[];
}

export interface IncomingInvite {
  invite_id: string;
  message: string | null;
  created_at: string;
  expires_at: string;
  invited_by: { first_name: string | null; last_name: string | null };
  consequences: {
    cancels_subscription: boolean;
    plan_code: string | null;
    provider: string | null;
    refund: 'none';
    /** The cancel is immediate now — remaining paid time is forfeited. */
    effective: 'immediately';
    /** What is forfeited: the paid-through date of the accepter's own plan. */
    current_period_end: string | null;
    /** Neither store lets a server cancel: the store keeps charging until the
     *  user stops the renewal there themselves (manage_url). Named exactly as
     *  the server sends it — a hand-written type that drifted from the payload
     *  (`_in_app_store`) kept this warning from ever rendering. */
    user_must_cancel_in_store: boolean;
    /** The plan of the household being joined (the yearly→monthly warning). */
    joining_plan_code: string | null;
    /** false → the accept is refused outright (OWNER_HAS_MEMBERS): grey the
     *  button out instead of collecting a confirm the server will reject. */
    can_accept: boolean;
    blocked_reason: 'OWNER_HAS_MEMBERS' | null;
  };
}

export function getHousehold(): Promise<HouseholdRead> {
  return apiRequest<HouseholdRead>('GET', '/v1/household');
}

export interface InviteCreated {
  invite_id: string;
  expires_at: string;
  /** Present when the invitee cannot accept as things stand — e.g. they own a
   *  household with members (INVITEE_OWNS_HOUSEHOLD_WITH_MEMBERS) and must
   *  wind it down first. The invite was still created and sent. */
  warnings?: { code: string; message: string }[];
}

/** One of phone or email is required; the refine rejects neither. Keyed. */
export function createInvite(
  invite: { invitee_phone_e164?: string; invitee_email?: string; message?: string },
  idempotencyKey: string,
): Promise<InviteCreated> {
  return apiRequest<InviteCreated>('POST', '/v1/household/invites', {
    body: invite,
    idempotencyKey,
    retryConflict: true,
  });
}

export function revokeInvite(inviteId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/v1/household/invites/${encodeURIComponent(inviteId)}`);
}

export function resendInvite(inviteId: string): Promise<void> {
  return apiRequest<void>('POST', `/v1/household/invites/${encodeURIComponent(inviteId)}/resend`);
}

export function declineInvite(inviteId: string): Promise<void> {
  return apiRequest<void>('POST', `/v1/household/invites/${encodeURIComponent(inviteId)}/decline`);
}

/**
 * The session is the accepter. `token` is OPTIONAL and omitted when we have
 * none: the backend accepts either proof — the token, or the session being the
 * invitee — and every invite in /v1/me/invites is matched to the session by
 * that same predicate, so a token is never needed for one of those. We still
 * send the deep-link stash when it exists rather than dropping it on the floor.
 * `confirm_cancel` exists because joining a household can cancel your own
 * subscription — the backend refuses with WOULD_CANCEL_SUBSCRIPTION until it is
 * set. Keyed.
 */
export function acceptInvite(
  inviteId: string,
  token: string | null,
  confirmCancel: boolean,
  idempotencyKey: string,
): Promise<unknown> {
  return apiRequest<unknown>(
    'POST',
    `/v1/household/invites/${encodeURIComponent(inviteId)}/accept`,
    {
      body: {
        ...(token === null || token.length === 0 ? {} : { token }),
        ...(confirmCancel ? { confirm_cancel: true } : {}),
      },
      idempotencyKey,
      retryConflict: true,
    },
  );
}

export interface MemberGuardian {
  /** OPTIONAL since 2026-09-01, and the form no longer asks for it: the
   *  guardian IS the caller, so the server reads the name from their own person
   *  row. An explicit one still wins, for any caller whose profile has no name
   *  on it yet. */
  name?: string;
  /** OPTIONAL — the server defaults it to 'parent'. The form still shows the
   *  choice, because 'parent' is an assumption on a legal record and the two
   *  other values must stay reachable. */
  relationship?: 'parent' | 'legal_guardian' | 'other';
}

export interface DeviceOnlyMember {
  first_name: string;
  last_name: string;
  /** Optional for a MINOR: their signer is the guardian, and a 13-year-old may
   *  hold no address at all. Still required for an adult, who signs for
   *  themselves — the server refuses an adult without one. */
  email?: string;
  date_of_birth: string;
  /** OPTIONAL when the date of birth is under 18 — omitted entirely, the server
   *  records the caller as guardian with their own name and relationship
   *  'parent'. Still REFUSED when the DOB is not a minor's. The server decides
   *  which (profile.is_minor against the age of majority); the form below only
   *  decides which fields to SHOW. */
  guardian?: MemberGuardian;
}

/** GET /v1/household/members/{personId} — where a member is up to, so a reload
 *  mid-loop resumes instead of restarting. Coverage and status only: no hit
 *  detail exists on this response (P20). */
export interface MemberState {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  is_minor: boolean;
  required_signer_role: 'self' | 'guardian' | null;
  email_verified: boolean;
  consent_status: string;
  quiz_submitted: boolean;
  /** FACES matched to them by attribution. Reads 0 until face processing
   *  finishes, so it must never drive the step — that loops forever. */
  attributed_photo_count: number;
  /** Photos this member is the SUBJECT of. This is the one `next: 'photos'`
   *  turns on. */
  photo_count: number;
  next: 'email_verification' | 'quiz' | 'consent' | 'enrolment' | 'photos' | 'done';
}

export function getMemberState(personId: string): Promise<MemberState> {
  return apiRequest<MemberState>(
    'GET',
    `/v1/household/members/${encodeURIComponent(personId)}`,
  );
}

/**
 * No phone field of any shape: a device-only member has no device. Keyed.
 * The shape is the handler's own return (src/household/service.ts:104): the
 * new person_id is what the family flow signs consent and enrols against.
 */
export function addDeviceOnlyMember(
  member: DeviceOnlyMember,
  idempotencyKey: string,
): Promise<{ person_id: string; seat_claimed: boolean; next: string }> {
  return apiRequest<{ person_id: string; seat_claimed: boolean; next: string }>(
    'POST',
    '/v1/household/members',
    {
      body: member,
      idempotencyKey,
      retryConflict: true,
    },
  );
}

/** Exactly one field. The legacy updateMonitoring took thirteen. */
export function setMonitoring(personId: string, monitoringEnabled: boolean): Promise<unknown> {
  return apiRequest<unknown>('PATCH', `/v1/household/members/${encodeURIComponent(personId)}`, {
    body: { monitoring_enabled: monitoringEnabled },
  });
}

export function removeMember(personId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/v1/household/members/${encodeURIComponent(personId)}`);
}

/** Unilateral by construction — reads the session's own membership. */
export function leaveHousehold(): Promise<void> {
  return apiRequest<void>('DELETE', '/v1/household/membership');
}

export function getMyInvites(): Promise<{ invites: IncomingInvite[] }> {
  return apiRequest<{ invites: IncomingInvite[] }>('GET', '/v1/me/invites');
}

/**
 * A MEMBER's score and recommendations, read by their manager.
 *
 * In the member namespace rather than as a query parameter on /v1/me/score — a
 * subject on a /v1/me/* path is a contradiction. Neither read carries hit
 * detail (P20): what the manager gets is coverage, counts and status.
 */
export interface MemberScoreRead {
  subject_person_id: string;
  /** The same object GET /v1/me/score returns, or null. */
  score: ScoreRead['score'] | null;
  scope_note: string | null;
  unavailable_reason: 'no_quiz_response' | 'quiz_outdated' | null;
}

/**
 * ALWAYS 200, SOMETIMES NULL — and this is the one place it differs from
 * getScore(), deliberately. A signed-in user with no quiz is an anomaly, so
 * /v1/me/score 404s NO_QUIZ_RESPONSE. A member mid-onboarding is the NORMAL
 * state, so P24 applies and `unavailable_reason` is a next step, not a failure.
 */
export function getMemberScore(personId: string): Promise<MemberScoreRead> {
  return apiRequest<MemberScoreRead>(
    'GET',
    `/v1/household/members/${encodeURIComponent(personId)}/score`,
  );
}

/**
 * `voice` is what decides whether these render as NAVIGATION or as
 * information, and it must be read before rendering:
 *
 *  - `self` — the reader can perform the actions. Items carry a `target`.
 *    Your own recommendations, and every ON-DEVICE member's, because the
 *    manager is the only person who can act for them.
 *  - `ask` — the reader cannot; the member must. Titles read "Ask Mel to…"
 *    and `target` is ABSENT ON EVERY ITEM. That absence is not an omission to
 *    work around: the one card with a target here would be respond_to_hits'
 *    `Reports`, which is a link into another adult's hit detail. Synthesising a
 *    target re-opens exactly what P20 forbids.
 */
export interface MemberRecommendationsRead {
  subject_person_id: string;
  voice: 'self' | 'ask';
  recommendations: Recommendation[];
}

export function getMemberRecommendations(personId: string): Promise<MemberRecommendationsRead> {
  return apiRequest<MemberRecommendationsRead>(
    'GET',
    `/v1/household/members/${encodeURIComponent(personId)}/recommendations`,
  );
}

/**
 * NARROWER THAN THE READS. The api authorises this one through
 * PhotoService.resolveSubject: yourself, or a subject with no account of their
 * own. Dismissing another adult's card is acting AS them, so an own-device
 * member answers 403 — show them the score and the reworded cards, and offer
 * no dismiss control.
 */
export function acknowledgeMemberRecommendation(
  personId: string,
  band: 'moderate' | 'high',
): Promise<void> {
  return apiRequest<void>(
    'POST',
    `/v1/household/members/${encodeURIComponent(personId)}/recommendations/acknowledge`,
    { body: { id: 'action_review_score', band } },
  );
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

export interface MintedUpload {
  upload_id: string;
  presigned_put_url: string;
  object_key: string;
  expires_at: string;
}

/** Off the projection in src/media/photos.ts; the handler types it `unknown`. */
export interface PhotoRow {
  photo_id: string;
  url: string;
  uploaded_at: string;
  width: number | null;
  height: number | null;
  protected: boolean;
  unprotected_reason: string | null;
  faces_total: number;
  faces_covered: number;
}

export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/heic', 'image/heif'] as const;
export type PhotoContentType = (typeof PHOTO_CONTENT_TYPES)[number];

export interface UploadRequest {
  content_sha256: string;
  byte_size: number;
  content_type: PhotoContentType;
  original_filename?: string;
}

/**
 * THE SUBJECT IS A PERSON, NOT AN ACCOUNT (spec 2026-09-02). Every photo route
 * takes an optional `subject_person_id`; omitted, the api defaults it to the
 * caller's own person, which is why every pre-existing call site here is
 * unchanged and still correct.
 *
 * It is NOT a relaxation of P1. The value is an INPUT the api authorises
 * against the session on every call (PhotoService.resolveSubject) — never a way
 * to address data. An unmanageable subject, a person outside the household and
 * a person id that does not exist all answer an identical `403 FORBIDDEN`, so
 * the parameter cannot be used to discover which ids are real. Do not try to
 * tell those three apart.
 */
export interface PhotoSubject {
  subject_person_id?: string;
}

/** NOT keyed — a repeat mint makes fresh pending rows, which are swept. */
export function mintUploads(
  files: UploadRequest[],
  subjectPersonId?: string,
): Promise<{ uploads: MintedUpload[] }> {
  return apiRequest<{ uploads: MintedUpload[] }>('POST', '/v1/photos/uploads', {
    body: { files, ...subjectBody(subjectPersonId) },
  });
}

/** The commit is what makes a photo real and enqueues work, so it IS keyed. */
export function commitPhotos(
  uploadIds: string[],
  idempotencyKey: string,
  subjectPersonId?: string,
): Promise<unknown> {
  return apiRequest<unknown>('POST', '/v1/photos', {
    body: {
      uploads: uploadIds.map((upload_id) => ({ upload_id })),
      // ON THE COMMIT AS WELL AS THE MINT. The api checks it on each, and a
      // mint authorised for one subject followed by a commit naming another is
      // exactly the mismatch that check exists for.
      ...subjectBody(subjectPersonId),
    },
    idempotencyKey,
    retryConflict: true,
  });
}

/** The api ECHOES the subject at the top level, on purpose: a client holding
 *  several members' grids at once reads which response it has from the body
 *  rather than tracking it alongside the request. */
export interface PhotoPage {
  subject_person_id: string;
  photos: PhotoRow[];
  next_cursor: string | null;
}

export function listPhotos(
  cursor?: string,
  limit?: number,
  subjectPersonId?: string,
): Promise<PhotoPage> {
  return apiRequest<PhotoPage>('GET', '/v1/photos', {
    query: { cursor, limit, subject_person_id: subjectPersonId },
  });
}

/** DELETE with a body, deliberately: a photo id in a URL lands in access logs. */
export function deletePhotos(photoIds: string[], subjectPersonId?: string): Promise<void> {
  return apiRequest<void>('DELETE', '/v1/photos', {
    body: { photo_ids: photoIds, ...subjectBody(subjectPersonId) },
  });
}

/**
 * An ABSENT key, never `subject_person_id: undefined`. The api parses every
 * inbound body with `.strict()`, and a key whose value is undefined survives
 * `JSON.stringify` as no key at all — but only by luck of the serialiser. Being
 * explicit here means the default-to-self case sends a body the api would
 * accept under any serialisation.
 */
function subjectBody(subjectPersonId: string | undefined): PhotoSubject {
  return subjectPersonId === undefined ? {} : { subject_person_id: subjectPersonId };
}

/** Mint → PUT each file straight to S3 → commit. Whole-batch, in order. */
export async function uploadPhotos(
  files: { file: File; sha256: string; contentType: PhotoContentType }[],
  idempotencyKey: string,
  onProgress?: (done: number, total: number) => void,
  subjectPersonId?: string,
): Promise<unknown> {
  const minted = await mintUploads(
    files.map((entry) => ({
      content_sha256: entry.sha256,
      byte_size: entry.file.size,
      content_type: entry.contentType,
      original_filename: entry.file.name,
    })),
    subjectPersonId,
  );
  for (const [index, upload] of minted.uploads.entries()) {
    const entry = files[index];
    if (entry === undefined) throw new Error('api minted more uploads than files were sent');
    await putToPresignedUrl(upload.presigned_put_url, entry.file, entry.contentType);
    onProgress?.(index + 1, minted.uploads.length);
  }
  return commitPhotos(
    minted.uploads.map((upload) => upload.upload_id),
    idempotencyKey,
    subjectPersonId,
  );
}

/**
 * WHICH CONTENT TYPE TO DECLARE for a file the user picked.
 *
 * The browser reports `''` for HEIC on some platforms, so the extension is the
 * fallback. The api enforces the enum either way — an unsupported type is
 * refused at the boundary rather than discovered by the decoder — and the type
 * declared here is the one the presigned PUT is signed for, so it must be the
 * same string on both calls or S3 answers SignatureDoesNotMatch.
 *
 * Lives beside sha256Hex because it is the same kind of thing: a browser-side
 * fact about a File that every upload surface needs and none of them should
 * each keep their own copy of.
 */
export function photoContentTypeOf(file: File): PhotoContentType | null {
  if ((PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as PhotoContentType;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.heic')) return 'image/heic';
  if (name.endsWith('.heif')) return 'image/heif';
  return null;
}

/**
 * The pfp enum is a DIFFERENT set from the photo one, deliberately: a profile
 * picture is a display asset that browsers must render inline, so it takes
 * webp and png and NOT heic — which most browsers cannot draw.
 */
export function avatarContentTypeOf(file: File): AvatarContentType | null {
  if ((AVATAR_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as AvatarContentType;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  return null;
}

/** The hash the api validates as 64 lowercase hex; the worker computes the real one. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------------------------
// Profile pictures
// ---------------------------------------------------------------------------

/**
 * A pfp is a DISPLAY asset and nothing else. It lives in its own bucket, it
 * never reaches Rekognition and it never becomes a search seed — and it is
 * never sourced from the enrolment reference image, which is Article 9
 * biometric data collected under a consent document naming a different purpose.
 *
 * NO BYTES CROSS THE API (P17). Route A is a server-side S3 CopyObject out of
 * one of the subject's own photos; route B is a presigned PUT the browser
 * performs. Nothing here decodes an image.
 */
export const AVATAR_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AvatarContentType = (typeof AVATAR_CONTENT_TYPES)[number];

export interface MintedAvatarUpload {
  upload_id: string;
  presigned_put_url: string;
  expires_at: string;
}

export interface AvatarUrl {
  pfp_url: string;
  pfp_expires_at: string;
}

/**
 * Signed for THAT content type and THAT exact byte count. A mismatch on the
 * PUT is refused by S3, not by us — so send back the same Content-Type that was
 * declared here, and do not re-encode between the two calls.
 */
export function mintAvatarUpload(
  contentType: AvatarContentType,
  byteSize: number,
  subjectPersonId?: string,
): Promise<MintedAvatarUpload> {
  return apiRequest<MintedAvatarUpload>('POST', '/v1/avatars/uploads', {
    body: { content_type: contentType, byte_size: byteSize, ...subjectBody(subjectPersonId) },
  });
}

/**
 * EXACTLY ONE of photo_id and upload_id — a zod UNION server-side, so "both"
 * and "neither" are both a 400. Two functions rather than one with two
 * optionals, so this client cannot express the refused shape at all.
 */
export function setAvatarFromPhoto(photoId: string, subjectPersonId?: string): Promise<AvatarUrl> {
  return apiRequest<AvatarUrl>('POST', '/v1/avatars', {
    body: { photo_id: photoId, ...subjectBody(subjectPersonId) },
  });
}

export function setAvatarFromUpload(
  uploadId: string,
  subjectPersonId?: string,
): Promise<AvatarUrl> {
  return apiRequest<AvatarUrl>('POST', '/v1/avatars', {
    body: { upload_id: uploadId, ...subjectBody(subjectPersonId) },
  });
}

/**
 * Idempotent 204 (P24), and A REAL CHOICE THAT STICKS: removal also stamps
 * avatar_updated_at, which is what stops the automatic first-photo pfp coming
 * back on the next upload.
 */
export function removeAvatar(subjectPersonId?: string): Promise<void> {
  return apiRequest<void>('DELETE', '/v1/avatars', {
    query: { subject_person_id: subjectPersonId },
  });
}

/**
 * Mint → PUT the bytes straight to S3 → commit. NO Idempotency-Key on this
 * path, unlike POST /v1/photos: the last call wins and the picture ends up
 * correct, so a retry is safe. It leaves an unreferenced object behind, which
 * is the bucket's lifecycle problem, not the caller's.
 */
export async function uploadAvatar(
  file: File,
  contentType: AvatarContentType,
  subjectPersonId?: string,
): Promise<AvatarUrl> {
  const minted = await mintAvatarUpload(contentType, file.size, subjectPersonId);
  await putToPresignedUrl(minted.presigned_put_url, file, contentType);
  return setAvatarFromUpload(minted.upload_id, subjectPersonId);
}

// ---------------------------------------------------------------------------
// Discovered clusters
// ---------------------------------------------------------------------------

export interface ClusterRow {
  cluster_id: string;
  household_id: string;
  status: string;
  label: string;
  external_user_id: string;
  expired: boolean;
}

export function listClusters(): Promise<{ clusters: ClusterRow[] }> {
  return apiRequest<{ clusters: ClusterRow[] }>('GET', '/v1/clusters');
}

/**
 * 202, not 201: the claim is accepted, not completed — the cluster stays
 * unclaimed until a consented enrolment verifies the face. No Idempotency-Key;
 * a double-tap yields a clean 409 from the unique indexes.
 */
export function claimCluster(clusterId: string, member: DeviceOnlyMember): Promise<unknown> {
  return apiRequest<unknown>('POST', `/v1/clusters/${encodeURIComponent(clusterId)}/claim`, {
    body: member,
  });
}

export function dismissCluster(clusterId: string): Promise<void> {
  return apiRequest<void>('DELETE', `/v1/clusters/${encodeURIComponent(clusterId)}`);
}

// ---------------------------------------------------------------------------
// Reports and hits
// ---------------------------------------------------------------------------

export interface HitMatch {
  match_id: string;
  source_domain: string | null;
  detected_at: string | null;
  match_status: string | null;
  match_action: string | null;
  match_lifecycle: string | null;
  resolved_at: string | null;
  provider_count: number | null;
  score: number | null;
}

export interface Hit {
  hit_id: string;
  report_id: string | null;
  source_photo_id: string | null;
  hit_status: string;
  /**
   * THE SUBJECT DECIDES (services' presentation rule, 2026-08-21). A
   * machine_triaged hit arrives as an 'ask' — the person in the photo is the
   * deciding human, and their answer writes the confirm/reject decision.
   *
   *   being_checked  not triaged yet, or the spend gate skipped it. May sit
   *                  here indefinitely, and that is not a bug: nothing in this
   *                  system auto-promotes, so no timeout or escalation belongs
   *                  in the UI either.
   *   ask            the question below is live
   *   finding        they answered yes
   *   dismissed      they answered no; retired from the default list by the api
   */
  card: 'being_checked' | 'ask' | 'finding' | 'dismissed';
  /**
   * AUTHORED BY THE API AND RENDERED VERBATIM. Never composed here: two of the
   * five severities mean the opposite of a match — likely_not_subject is a
   * face-match that RAN AND FAILED, unassessed is no check at all — so a
   * client-side sentence could assert a match the system never made. Null
   * unless `card` is 'ask'.
   */
  question: string | null;
  /**
   * Advisory: false means the preview 404s PREVIEW_UNAVAILABLE, which is the
   * NORMAL case for an untriaged hit and for "no face found". Skipping the call
   * keeps two of the subject's 200 daily renders.
   */
  preview_expected: boolean;
  /** Whether they may still answer. */
  answerable: boolean;
  /**
   * LEGACY two-value projection of `card`, derived from it by the api so the
   * two cannot drift. Kept for the older row rendering; read `card` instead.
   */
  finding_state: 'confirmed' | 'being_checked';
  /** Urgency copy input, and it arrives ONLY on a hit decided as a finding —
   *  the api withholds review-band vocabulary before that. */
  severity: string | null;
  decided_at: string | null;
  last_checked_at: string | null;
  /**
   * The page the match was found on. Non-null ONLY once the subject has marked
   * the hit as abuse (`hit_status === 'acknowledged'`, i.e. after PATCH
   * {action: 'infringement'}); null on an ask-card, "usage is OK" and "not me".
   * May be a direct image address when the provider gave no backlink — open it
   * in the system browser, never preload it in a webview.
   */
  page_url: string | null;
  matches: HitMatch[];
}

/** Their machine classification is never edited by the answer; it can be null. */
export interface DecisionResult {
  hit_id: string;
  decision: 'confirmed' | 'rejected';
  severity: string | null;
  /** The subject re-sending their OWN identical answer. Success, not a clash. */
  idempotent_replay: boolean;
  card: Hit['card'];
}

/** The four outcomes (0026). NOT interchangeable — see the note on
 *  `ReportListItem.outcome`. */
export type ReportOutcome = 'findings' | 'clean' | 'not_due' | 'incomplete';

export interface ReportListItem {
  report_id: string;
  /** `YYYY-MM-DD`, UTC. A date string, never a timestamp: the week boundaries
   *  are UTC and parsing these into a local Date shifts them by a day. */
  period_start: string;
  period_end: string;
  /**
   * ONLY `clean` MAY BE RENDERED AS AN ABSENCE CLAIM.
   *
   * `clean` means a scan ran AND a provider answered and found nothing —
   * "no matches in monitored sources". `not_due` means no scan was due (the
   * person is on fortnightly or monthly cadence) and `incomplete` means a scan
   * ran but every provider failed. Rendering either of those as "nothing found"
   * tells somebody nobody looked that they are clear, which is the false
   * negative docs/CLAUDE.md §1 calls a broken promise.
   */
  outcome: ReportOutcome;
  hits_found: number;
  /** The first report is a PARTIAL week, dated from the first scan rather than
   *  from that week's Monday. */
  is_first_report: boolean;
  opened_at?: string | null;
}

/** Unlocked: the periods and the scope note. */
export interface ReportsList {
  reports: ReportListItem[];
  scope_note: string;
  locked?: false;
}

/**
 * LOCKED IS A 200, NOT A 402 (0026). The four report DETAIL routes still answer
 * 402; the list sells instead of refusing, because the digest email already told
 * this person to subscribe. Counts travel; nothing that says WHERE does.
 */
export interface LockedReportsList {
  locked: true;
  locked_reason: string | null;
  reports: Omit<ReportListItem, 'opened_at'>[];
  upsell: { headline: string; body: string; cta: string };
  scope_note: string;
}

export type ReportsRead = ReportsList | LockedReportsList;

export function isLocked(read: ReportsRead): read is LockedReportsList {
  return read.locked === true;
}

/** Whether the reports surface is open to this person, and why not (0023). */
export interface Entitlement {
  reports: 'locked' | 'unlocked';
  locked_reason: string | null;
}

export interface HomeRead {
  score: ScoreSummary | null;
  entitlement: Entitlement;
  /** The covering subscription's ending state — what the dashboard warns from.
   *  Dates and a status only; `paid` is false on the free plan. */
  subscription: {
    plan_code: string;
    paid: boolean;
    status: string;
    trial_ends_at: string | null;
    current_period_end: string | null;
    cancel_at: string | null;
  } | null;
  /** `unresolved_matches` is NULL while locked — withheld, never zeroed. */
  household_summary: { members: number; covered: number; unresolved_matches: number | null };
  reports_summary: {
    // Both null while locked: zero is a claim about what was found.
    active_hits: number | null;
    live_exposure_count: number | null;
    last_run_at: string | null;
    first_scan_completed_at: string | null;
    monitored_sources: number | null;
    scope_note: string;
    // The same scan truth GET /v1/reports carries — the dashboard's monitoring
    // copy is derived from these, never asserted.
    scan_state: string;
    scan_tier: string | null;
    next_scan_after: string | null;
    unavailable_reason: string | null;
  };
}

/** All three actions terminate: reporting abuse never costs more than clearing. */
export type MatchAction = 'infringement' | 'not_infringement' | 'not_me';

export function getHome(): Promise<HomeRead> {
  return apiRequest<HomeRead>('GET', '/v1/home');
}

/** The dated weekly list (0026). Answers 200 whether or not the caller has a
 *  paid plan — check `isLocked`. */
export function listReports(): Promise<ReportsRead> {
  return apiRequest<ReportsRead>('GET', '/v1/reports');
}

/**
 * reportId narrows within the session's own hits; it never widens.
 *
 * SINCE 0026 IT REALLY NARROWS: the id resolves to a period on media.reports and
 * filters by detected_at. An unknown or foreign id returns an empty list rather
 * than the person's whole history.
 */
export function getHits(reportId: string): Promise<{ hits: Hit[]; scope_note: string }> {
  return apiRequest<{ hits: Hit[]; scope_note: string }>(
    'GET',
    `/v1/reports/${encodeURIComponent(reportId)}/hits`,
  );
}

/**
 * The subject's blurred face crop, as bytes.
 *
 * CALLER OWNS THE OBJECT URL. This returns a Blob; whoever turns it into a URL
 * must revoke it, because an object URL outlives the component that made it and
 * this one points at a picture of somebody's abuse.
 *
 * `reveal` is that user's explicit per-item tap on THIS hit and nothing else —
 * never a default, never remembered, never applied to a list (services
 * invariant #23). Each call is a separate render against their ceiling (200 per
 * rolling 24 hours, counting ATTEMPTS), so a blurred load plus a reveal is two.
 *
 * There is no user_ref parameter and there must never be one: the api takes the
 * subject from the session, and a client-supplied one would be an IDOR against
 * non-consensual intimate imagery.
 */
export function getHitPreview(hitId: string, reveal: boolean): Promise<Blob> {
  return apiBlob(`/v1/hits/${encodeURIComponent(hitId)}/preview`, { reveal });
}

/**
 * The answer IS the decision (`decided_by='subject'`), and it moves the
 * protection score on the services side under cause_kind='subject_decision'.
 *
 * Two values only. "Not sure" is not a decision: leave the hit undecided and
 * let them answer later. No Idempotency-Key — the api sends none either,
 * because their endpoint is idempotent on the answer itself.
 */
export function decideHit(
  hitId: string,
  decision: 'confirmed' | 'rejected',
): Promise<DecisionResult> {
  return apiRequest<DecisionResult>('POST', `/v1/hits/${encodeURIComponent(hitId)}/decision`, {
    body: { decision },
  });
}

export function actOnHit(
  hitId: string,
  action: MatchAction,
  note?: string,
): Promise<{ hit: unknown }> {
  return apiRequest<{ hit: unknown }>('PATCH', `/v1/hits/${encodeURIComponent(hitId)}`, {
    body: note === undefined || note.length === 0 ? { action } : { action, note },
  });
}

// ---------------------------------------------------------------------------
// Action cards, formerly relayed by src/protection/ (GET
// /v1/protection/recommendations). The threat-context banner
// (getProtectionThreats/ProtectionThreat, GET /v1/protection/threats) is off
// this release line — src/protection/ stays on main only, docs/CLAUDE.md §6.
//
// GET /v1/recommendations replaced the retired GET /v1/protection/recommendations
// relay — this repo's own seven-card catalog plus the server-derived
// respond_to_hits item, never a relay of a services view. The score reads
// that used to live here — GET /v1/protection/score and
// /v1/protection/score/events — are RETIRED; the one score is
// GET /v1/me/score above.
// ---------------------------------------------------------------------------

/** Mirrors src/recommendations/catalog.ts's RecommendationId exactly — the
 *  business team's seven ids plus the one server-derived item. Not free to
 *  extend on this side: the server never emits an id outside this union. */
export type RecommendationId =
  | 'action_verify_email'
  | 'action_upload_photos'
  | 'action_quiz_refresh'
  | 'action_review_score'
  | 'action_complete_profile'
  | 'action_work_school'
  | 'action_take_quiz'
  | 'respond_to_hits';

/** Mirrors src/recommendations/catalog.ts's RecommendationTarget. A screen
 *  name, not a URL — this demo maps it to its own hash routes, and renders
 *  the card without a link for any target this client has no screen for
 *  (EmailVerification: resending lives inline on the dashboard, not its own
 *  screen). */
export type RecommendationTarget =
  | 'EmailVerification'
  | 'Photos'
  | 'LikenessHealthQuiz'
  | 'LikenessScoreDetail'
  | 'Profile'
  | 'ProfileOptional'
  | 'Reports';

export interface Recommendation {
  id: RecommendationId;
  title: string;
  description: string;
  urgent: boolean;
  /**
   * OPTIONAL, because the server's own return type is
   * `Omit<Recommendation,'target'>[] | Recommendation[]`: the `ask` voice drops
   * the target from EVERY item (see getMemberRecommendations). Declaring it
   * required would be a type that lies about half the responses, and the lie
   * would surface as a card linking a manager into another person's report.
   *
   * Always present on GET /v1/recommendations — that read is always `self`.
   */
  target?: RecommendationTarget;
  params: Record<string, unknown>;
}

/** The seven business-defined cards plus respond_to_hits, in server order.
 *  Open cards only; completion is detected server-side and the card vanishes. */
export function getRecommendations(): Promise<{ recommendations: Recommendation[] }> {
  return apiRequest<{ recommendations: Recommendation[] }>('GET', '/v1/recommendations');
}

/** The one write this surface has: "I have read my risk band", scoped to the
 *  band shown so a drop from moderate to high re-raises the card. */
export function acknowledgeRecommendation(band: 'moderate' | 'high'): Promise<void> {
  return apiRequest<void>('POST', '/v1/recommendations/acknowledge', {
    body: { id: 'action_review_score', band },
  });
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

/** Same port as /v1 for the api deployable (src/apps/api/main.ts). */
export function getHealthz(): Promise<{ status: string }> {
  return apiRequest<{ status: string }>('GET', '/healthz', { anonymous: true });
}

/** 503 NOT_READY until the three required svc contract views answer. */
export function getReadyz(): Promise<{ status: string }> {
  return apiRequest<{ status: string }>('GET', '/readyz', { anonymous: true });
}

export { newIdempotencyKey };
